import React from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  ArrowRight,
  RefreshCcw,
  Briefcase,
  ChevronRight,
} from "lucide-react";

// --- PREMIUM SOFT THEME ---
const THEME = {
  primary: "#f97335",
  primarySoft: "rgba(249,115,53,0.12)",
  dark: "#111827",
  text: "#374151",
  muted: "#6B7280",
  white: "#FFFFFF",
  background: "#f8fafc",
  cardBorder: "rgba(15,23,42,0.04)",
};

const ServicesPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: "local",
      title: "Local Trip",
      description: "Hourly rentals for city travel & errands",
      icon: <MapPin size={24} color={THEME.primary} strokeWidth={2.3} />,
      path: "/book-ride?type=local",
    },
    {
      id: "oneway",
      title: "Oneway Trip",
      description: "Drop to your destination, no return fare",
      icon: <ArrowRight size={24} color={THEME.primary} strokeWidth={2.3} />,
      path: "/book-ride?type=oneway",
    },
    {
      id: "roundtrip",
      title: "Round Trip",
      description: "Outstation travel with return included",
      icon: <RefreshCcw size={24} color={THEME.primary} strokeWidth={2.3} />,
      path: "/book-ride?type=roundtrip",
    },
    {
      id: "package",
      title: "Taxi Package",
      description: "Custom tours & multi-day packages",
      icon: <Briefcase size={24} color={THEME.primary} strokeWidth={2.3} />,
      path: "/book-ride?type=package",
    },
  ];

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)",
      }}
    >
      {/* TOP HERO SECTION */}
      <div className="px-3 pt-3 pb-3">
        <div
          className="position-relative overflow-hidden rounded-5 p-3"
          style={{
            background:
              "linear-gradient(135deg, #fed7aa 0%, #fb923c 50%, #ea580c 100%)",
            boxShadow: "0 18px 45px rgba(37,99,235,0.16)",
          }}
        >
          {/* Decorative Blur */}
          <div
            className="position-absolute"
            style={{
              width: "180px",
              height: "180px",
              background: "rgba(255,255,255,0.08)",
              borderRadius: "999px",
              top: "-60px",
              right: "-60px",
              filter: "blur(4px)",
            }}
          />

          <div className="position-relative">
            <h3
              className="fw-bold text-dark mb-2"
              style={{
                lineHeight: 1.25,
                letterSpacing: "-0.5px",
              }}
            >
              Book Your Perfect Ride
            </h3>

            <p
              className="mb-0"
              style={{
                color: "rgba(0, 0, 0, 0.78)",
                fontSize: "0.92rem",
                maxWidth: "280px",
              }}
            >
              Safe, comfortable and premium travel experiences across Odisha.
            </p>
          </div>
        </div>
      </div>

      {/* SERVICE LIST */}
      <div className="px-3 pb-4">
        <div className="d-flex flex-column gap-3">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white rounded-5 p-3 d-flex align-items-center"
              style={{
                border: `1px solid ${THEME.cardBorder}`,
                boxShadow: "0 6px 24px rgba(15,23,42,0.05)",
                cursor: "pointer",
                transition: "all 0.18s ease",
                backdropFilter: "blur(10px)",
              }}
              onClick={() => navigate(service.path)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 14px 30px rgba(15,23,42,0.09)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 24px rgba(15,23,42,0.05)";
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = "scale(0.985)";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.transform = "scale(0.985)";
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              {/* ICON */}
              <div
                className="d-flex align-items-center justify-content-center me-3"
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "20px",
                  background: THEME.primarySoft,
                  flexShrink: 0,
                }}
              >
                {service.icon}
              </div>

              {/* TEXT */}
              <div className="flex-grow-1 pe-2">
                <h6
                  className="fw-bold mb-1"
                  style={{
                    color: THEME.dark,
                    fontSize: "1rem",
                    letterSpacing: "-0.2px",
                  }}
                >
                  {service.title}
                </h6>

                <p
                  className="mb-0"
                  style={{
                    color: THEME.muted,
                    fontSize: "0.83rem",
                    lineHeight: 1.4,
                  }}
                >
                  {service.description}
                </p>
              </div>

              {/* RIGHT ARROW */}
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "999px",
                  background: "#f8fafc",
                }}
              >
                <ChevronRight size={18} color={THEME.muted} opacity={0.7} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* BOTTOM BANNER */}
      <div className="px-3">
        <div
          className="overflow-hidden position-relative"
          style={{
            borderRadius: "28px",
            background:
              "linear-gradient(135deg, rgba(14,165,233,0.10) 0%, rgba(59,130,246,0.08) 100%)",
          }}
        >
          <div className="p-3 pb-0 text-center">
            <p
              className="fw-semibold mb-1"
              style={{
                color: "#0284c7",
                fontSize: "0.72rem",
                letterSpacing: "1.4px",
                textTransform: "uppercase",
              }}
            >
              Explore Odisha with Patra Travels
            </p>

            <h6
              className="fw-bold mb-2"
              style={{
                color: THEME.dark,
              }}
            >
              Premium Travel Experience
            </h6>
          </div>

          <img
            src="/Odisha-city-skyline-02-small.jpg"
            alt="Odisha Skyline"
            style={{
              width: "100%",
              display: "block",
              objectFit: "contain",
              opacity: 0.96,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;